import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider.js';
import axios from 'axios';
import configData from './Config';
import 'react-datepicker/dist/react-datepicker.css';
import ReactToPrint from 'react-to-print';
import { unitsofmeasurement, getItemCost, getProductionCost, getItemPrice } from './Utils/items.js';
import MarginBar from './Components/marginbar.js';
import { isValidNumber, parseAmount } from './Utils/textparse.js';
import { getExpenseBudget } from './Utils/chartaccounts.js';

import { getPermission } from './Utils/permissions.js';
import Select from './Components/select.js';

import Title from './Title.js';

import './ProductionOrders.css';

export const accountTypes = [
    { id: 'expense', name: 'Expense' },
    { id: 'item', name: 'Item' },
];

function ProductionOrders({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const posmargin = project?.pos?.margin || 0;

    const [filteredproductionorders, setFilteredproductionorders] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        console.log(project)
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filtered = project.productionorders.filter((invoice) =>
            invoice.itemid.toLowerCase().includes(search.toLowerCase())
        );

        setFilteredproductionorders(filtered);
    }, [project, search]);

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const addButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddProductionOrder />, modaltype: 'large', modaltitle: 'Add Production Order' }));
    }

    const editButton = (productionorder) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditProductionOrder productionorder={productionorder} mode='edit' />, modaltype: 'large', modaltitle: 'Edit Production Order' }));
    }

    const viewButton = (productionorder) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewProductionOrder productionorder={productionorder} />, modaltype: 'large', modaltitle: 'View Production Order' }));
    }

    const cloneButton = (productionorder) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditProductionOrder productionorder={productionorder} mode='clone' />, modaltype: 'large', modaltitle: 'Clone Production Order' }));
    }

    const removeButton = (productionorder) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveProductionOrder productionorder={productionorder} />, modaltype: 'small', modaltitle: 'Remove Production Order' }));
    }

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!project || !project.productionorders || project.productionorders.length === 0) {
                setResult('No data to export');
                return;
            }

            const importedContent = JSON.stringify(project.productionorders, null, 2);

            navigator.clipboard.writeText(importedContent)
                .then(() => setResult('Data copied to clipboard.'))
                .catch(err => setResult('Error copying data to clipboard:', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (isJSON) {
                setResult('Invalid format')
            }

            if (!isJSON) {
                const requiredAttributes = ["Date", "Supplier", "Name", "Amount"];
                let rows = importText.split('\n').map(row => row.split('\t'));
                const headers = rows.shift().map(header => header.trim());

                const missingHeaders = requiredAttributes.filter(attr => !headers.includes(attr));

                if (headers.length === 0 || headers.length !== requiredAttributes.length || missingHeaders.length > 0) {
                    setResult(`Invalid headers detected. Missing headers: ${missingHeaders.join(', ')}`);
                    return;
                }

                if (rows.length === 0) {
                    setResult('No data rows detected.');
                    return;
                }

                data = rows.map(row => {
                    let entry = {};
                    headers.forEach((header, index) => {
                        entry[header] = row[index]?.trim() || '';
                    });
                    return entry;
                });

                const convertedEntries = [];

                for (let i = 0; i < data.length; i++) {
                    const entry = data[i];
                    let convertedEntry = {};

                    const matchingSupplier = project.suppliers.find(supplier => supplier.name.toLowerCase() === entry['Supplier'].toLowerCase());
                    if (!matchingSupplier) {
                        setResult("Supplier not found for \"" + entry['Supplier'] + "\" at row " + i);
                        break;
                    }

                    const matchingSubaccount = project.subaccounts.find(subaccount => subaccount.name.toLowerCase() === entry['Subaccount'].toLowerCase());
                    if (!matchingSubaccount) {
                        setResult("Subaccount not found for \"" + entry['Subaccount'] + "\" at row " + i);
                        break;
                    }

                    const parsedAmount = parseAmount(entry['Amount']);
                    if (!parsedAmount.currency) {
                        console.log("Currency not found in amount:", parsedAmount.amount);
                        break;
                    }
                    const matchingCurrency = project.currencies.find(currency =>
                        currency.name.toLowerCase() === parsedAmount.currency.toLowerCase() ||
                        currency.symbol.toLowerCase() === parsedAmount.currency.toLowerCase()
                    );
                    if (!matchingCurrency) {
                        setResult("Currency not found for \"" + parsedAmount.currency + "\" at row " + i);
                        break;
                    }

                    convertedEntry = {
                        ts: Math.floor(new Date(entry['Date']).getTime() / 1000),
                        supplierid: matchingSupplier.id,
                        name: entry['Name'],
                        amount: parsedAmount.amount,
                        currencyid: matchingCurrency.id
                    };
                    convertedEntries.push(convertedEntry);
                }
                console.log(convertedEntries)
                if (convertedEntries.length === data.length) {
                    setNewEntries(convertedEntries);
                    setResult(`Data is valid. Entries found: ${convertedEntries.length}`);
                }
            }
        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            if (newEntries.length == 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);
            setResult(null);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                productionorders: newEntries
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTPRODUCTIONORDERS, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_PRODUCTIONORDERS',
                            productionorders: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });

            setResult('Data imported successfully.');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function NewItem({ item, index, handleItemChange, handleQuantityChange, handleUOMChange, handleRemoveItem }) {
        return (
            <tr>
                <td>
                    <Select data={project.items} itemid={item.accountid} setItemid={(itemId) => handleItemChange({ target: { value: itemId } }, index)} />
                </td>
                <td>
                    <input
                        className='modal_input'
                        type="number"
                        value={item.itemquantity}
                        onChange={(e) => handleQuantityChange(e, index)}
                    />
                </td>
                <td>
                    <Select data={unitsofmeasurement} itemid={item.itemuom} setItemid={(itemId) => handleUOMChange({ target: { value: itemId } }, index)} />
                </td>
                <td>
                    <div className='modal_removeitembutton' onClick={() => handleRemoveItem(index)}>&minus;</div>
                </td>
            </tr>
        );
    }

    function AddProductionOrder() {
        const [itemid, setItemid] = useState('');

        const [items, setItems] = useState([{ accountid: '', itemquantity: 1, itemuom: 'pcs' }]);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        const handleAddItem = () => {
            setItems([...items, { accountid: '', itemquantity: 1, itemuom: 'pcs' }])
        };

        const handleItemChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accountid = selectedItem;
            setItems(updatedItems);
        };

        const handleQuantityChange = (event, index) => {
            const selectedItem = isValidNumber(event.target.value) ? event.target.value : 0;
            const updatedItems = [...items];
            updatedItems[index].itemquantity = selectedItem;
            setItems(updatedItems);
        };

        const handleUOMChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemuom = selectedItem;
            setItems(updatedItems);
        };

        const handleRemoveItem = (indexToRemove) => {
            if (items.length > 1) {
                const updateItems = items.filter((_, index) => index !== indexToRemove);
                setItems(updateItems);
            }
        };

        const addProductionorder = () => {
            console.log('Trying to add production order');

            setResult('');

            for (let item of items) {
                if (!item.accountid) {
                    setResult('Missing item id in items');
                    return;
                }
            }

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Production Orders',
                'add',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!itemid) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                itemid: itemid,
                items: items,
            }

            axios.post(configData.CONTROLLERURL + configData.ADDPRODUCTIONORDER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add production order data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_PRODUCTIONORDER',
                            productionorder: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Item</div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <Select data={project.items} itemid={itemid} setItemid={setItemid} />
                    </div>
                    <table className='modal_table'>
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Quantity</th>
                                <th>Unit of measurement</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <NewItem
                                    key={'item' + index}
                                    item={item}
                                    index={index}
                                    handleItemChange={handleItemChange}
                                    handleQuantityChange={handleQuantityChange}
                                    handleUOMChange={handleUOMChange}
                                    handleRemoveItem={handleRemoveItem}
                                />
                            ))}
                        </tbody>
                    </table>
                    <div className="modal_actions">
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <div className='modal_button' onClick={handleAddItem}>Add item</div>
                            </div>
                            <div className='modal_buttoncontainer'></div>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addProductionorder()} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function EditProductionOrder({ productionorder, mode }) {
        const [id, setId] = useState('');

        const [itemid, setItemid] = useState('');

        const [items, setItems] = useState([{ itemid: '', itemquantity: 0, itemuom: 'pcs' }]);

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            if (productionorder) {
                const productionOrdercopy = {
                    ...productionorder,
                    items: Array.isArray(productionorder.items) ? productionorder.items.map(item => ({ ...item })) : []
                };
                setId(productionOrdercopy.id)
                setItemid(productionOrdercopy.itemid)
                setItems(productionOrdercopy.items)
            }
        }, [productionorder]);

        const handleAddItem = () => {
            setItems([...items, { accountid: '', itemquantity: 0, itemuom: 'pcs' }])
        };

        const handleItemChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].accountid = selectedItem;
            setItems(updatedItems);
        };

        const handleQuantityChange = (event, index) => {
            const selectedItem = isValidNumber(event.target.value) ? event.target.value : 0;
            const updatedItems = [...items];
            updatedItems[index].itemquantity = selectedItem;
            setItems(updatedItems);
        };

        const handleUOMChange = (event, index) => {
            const selectedItem = event.target.value;
            const updatedItems = [...items];
            updatedItems[index].itemuom = selectedItem;
            setItems(updatedItems);
        };

        const handleRemoveItem = (indexToRemove) => {
            if (items.length > 1) {
                const updateItems = items.filter((_, index) => index !== indexToRemove);
                setItems(updateItems);
            }
        };

        const updateProductionorder = (id, itemid, items) => {
            console.log('Trying to update production order');

            setResult('');

            for (let item of items) {
                if (!item.accountid) {
                    setResult('Missing account id in items');
                    return;
                }
            }

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Production Orders',
                'update',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!itemid) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id,
                itemid: itemid,
                items: items,
            }

            axios.post(configData.CONTROLLERURL + configData.UPDATEPRODUCTIONORDER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Update production order data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'UPDATE_PRODUCTIONORDER',
                            productionorder: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        const addProductionorder = (itemid, items) => {
            console.log('Trying to add production order');

            setResult('');

            for (let item of items) {
                if (!item.accountid) {
                    setResult('Missing item id in items');
                    return;
                }
            }

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Production Orders',
                'add',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!itemid) {
                setResult('Missing info');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                itemid: itemid,
                items: items,
            }

            axios.post(configData.CONTROLLERURL + configData.ADDPRODUCTIONORDER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add production order data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_PRODUCTIONORDER',
                            productionorder: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Item</div>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <Select data={project.items} itemid={itemid} setItemid={setItemid} />
                    </div>
                    <table className='modal_table'>
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Quantity</th>
                                <th>Unit of measurement</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                items.map((item, index) => (
                                    <NewItem
                                        key={'item' + index}
                                        item={item}
                                        index={index}
                                        handleItemChange={handleItemChange}
                                        handleQuantityChange={handleQuantityChange}
                                        handleUOMChange={handleUOMChange}
                                        handleRemoveItem={handleRemoveItem}
                                    />
                                ))
                            }
                        </tbody>
                    </table>
                    <div className="modal_actions">
                        <div className='modal_buttons'>
                            <div className='modal_buttoncontainer'>
                                <div className='modal_button' onClick={handleAddItem}>Add item</div>
                            </div>
                            <div className='modal_buttoncontainer'></div>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            {mode === 'edit' && <button className="modal_button" onClick={() => updateProductionorder(id, itemid, items)} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>}
                            {mode === 'clone' && <button className="modal_button" onClick={() => addProductionorder(itemid, items)} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>}
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeButton(productionorder)}>Remove</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function RemoveProductionOrder({ productionorder }) {
        const [id, setId] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(productionorder.id)
        }, [productionorder]);

        const removeProductionorder = () => {
            console.log('Trying to remove production order');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Production Order',
                'remove',
                project.archived
            );
            if (hasPermission.code == 0) {
                setResult(hasPermission.data);
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id
            }

            axios.post(configData.CONTROLLERURL + configData.REMOVEPRODUCTIONORDER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove production order data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'REMOVE_PRODUCTIONORDER',
                            productionorderid: id
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeProductionorder()} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function ViewProductionOrder({ productionorder }) {
        const [id, setId] = useState('');

        const [itemid, setItemid] = useState('');
        const [items, setItems] = useState([]);

        let itemname = project.items.find((item) => item.id === productionorder.itemid)?.name;

        const expensebudget = getExpenseBudget({
            expensegroups: project.expensegroups,
            expenseaccounts: project.expenseaccounts,
            currencies: project.currencies,
            exchangerates: project.exchangerates,
            currencyid: project.basecurrency
        });

        const productioncost = getProductionCost(
            itemid,
            project.purchaseinvoices,
            project.productionorders,
            project.exchangerates,
            project.basecurrency
        )

        const totalBudget = expensebudget.find(e => e.type === 'expensetotal')?.budget || 0;

        const itemprice = getItemPrice(
            project.items,
            itemid,
            project.exchangerates,
            project.basecurrency
        )

        const printRef = useRef();

        useEffect(() => {
            setId(productionorder.id);
            setItemid(productionorder.itemid);
            setItems(productionorder.items);
        }, [productionorder]);

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>ID</div>
                        </div>
                        <div className='modal_rowsection'>{id}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Item</div>
                        </div>
                        <div className='modal_rowsection'>{itemname}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <table className='modal_table'>
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Quantity</th>
                                        <th>Unit of measurement</th>
                                        <th>Cost</th>
                                        <th>Net</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        items.map((item, index) => {
                                            let foundItem = project.items.find((it) => it.id === item.accountid);
                                            const itemcost = parseFloat(
                                                getItemCost(item.accountid, project.purchaseinvoices, project.exchangerates, item.itemuom, project.basecurrency)
                                            ) || 0;
                                            const quantity = parseFloat(item.itemquantity) || 0;
                                            let currencySymbol = project.currencies.find(curr => curr.id == project.basecurrency)?.symbol;
                                            const total = quantity * itemcost;

                                            return (
                                                <tr key={'item' + index}>
                                                    <td>{foundItem?.name}</td>
                                                    <td>{item.itemquantity}</td>
                                                    <td>{item.itemuom}</td>
                                                    <td>{currencySymbol} {itemcost.toFixed(5)}</td>
                                                    <td>{currencySymbol} {total.toFixed(5)}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={4}>Total</td>
                                        <td>
                                            {
                                                project.currencies.find(c => c.id == project.basecurrency)?.symbol
                                                + ' ' +
                                                productioncost.toFixed(5)
                                            }
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Margin Bar</div>
                        </div>
                        <div className='modal_rowsection'>
                            <MarginBar cost={productioncost} price={itemprice} budget={totalBudget} margin={posmargin} />
                        </div>
                        <div className='modal_rowsection'>
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => cloneButton(productionorder)}>Clone</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='productionorders'>
            <Title text='Production Orders' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={addButton}>Add Production Order</button>
                </div>
                <div className='buttonsection'>
                    <input
                        className='input'
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
            </div>

            <table className='table'>
                <thead><tr><th></th><th></th><th>ID</th><th>Item</th></tr></thead>
                <tbody>
                    {
                        filteredproductionorders.sort((a, b) => b.ts - a.ts).map((productionorder, index) => {
                            let itemname = project.items.find((item) => item.id === productionorder.itemid)?.name;
                            return (
                                <tr key={'productionorder' + index}>
                                    <td><div className='table_button' onClick={() => editButton(productionorder)}>Edit</div></td>
                                    <td><div className='table_button' onClick={() => viewButton(productionorder)}>View</div></td>
                                    <td><div>{productionorder.id}</div></td>
                                    <td><div>{itemname}</div></td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </table>
        </div>
    );
}

export default ProductionOrders;